import { useState, useRef, useEffect } from 'react'
import type { CompareMode, ManagerInfo } from '../types'

interface CompareSelectorProps {
  mode: CompareMode
  managers: ManagerInfo[]
  onCompare: (mode: CompareMode) => void
}

const PRESET_MODES = [
  { value: 'none', label: 'No Comparison' },
  { value: 'autopick', label: 'vs Autopick' },
] as const

function getDisplayLabel(mode: CompareMode, managers: ManagerInfo[]): string {
  if (mode === 'none') return 'No Comparison'
  if (mode === 'autopick') return 'vs Autopick'
  const manager = managers.find(m => m.id === mode)
  return manager ? `vs ${manager.displayName}` : 'No Comparison'
}

export function CompareSelector({ mode, managers, onCompare }: CompareSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  function handleSelect(value: CompareMode) {
    onCompare(value)
    setIsOpen(false)
  }

  const displayLabel = getDisplayLabel(mode, managers)
  const isComparing = mode !== 'none'

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium
                    transition-colors cursor-pointer
                    ${isComparing
                      ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300'
                      : 'border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300'
                    }
                    hover:border-zinc-300 dark:hover:border-zinc-600`}
      >
        <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <path d="M4 2v12M12 2v12M1 6h6M9 10h6" />
        </svg>
        <span className="truncate max-w-[160px]">{displayLabel}</span>
        <svg className={`w-3.5 h-3.5 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M4 6l4 4 4-4" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 z-50 w-64
                        rounded-xl border border-zinc-200 dark:border-zinc-700
                        bg-white dark:bg-zinc-900 shadow-lg shadow-zinc-200/50 dark:shadow-zinc-950/50
                        overflow-hidden">
          <div className="py-1">
            {PRESET_MODES.map(preset => (
              <button
                key={preset.value}
                type="button"
                onClick={() => handleSelect(preset.value)}
                className={`w-full text-left px-3.5 py-2 text-sm cursor-pointer
                            transition-colors
                            ${mode === preset.value
                              ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 font-medium'
                              : 'text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'}`}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {managers.length > 0 && (
            <>
              <div className="border-t border-zinc-100 dark:border-zinc-800" />
              <div className="py-1">
                <div className="px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider
                                text-zinc-400 dark:text-zinc-500">
                  League Managers
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {managers.map(manager => (
                    <button
                      key={manager.id}
                      type="button"
                      onClick={() => handleSelect(manager.id)}
                      className={`w-full text-left px-3.5 py-2 text-sm cursor-pointer
                                  transition-colors flex items-center justify-between
                                  ${mode === manager.id
                                    ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 font-medium'
                                    : 'text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'}`}
                    >
                      <span className="flex items-center gap-2">
                        <span className="flex items-center justify-center w-6 h-6 rounded-full
                                        bg-zinc-100 dark:bg-zinc-800 text-[10px] font-bold
                                        text-zinc-600 dark:text-zinc-400">
                          {manager.initials}
                        </span>
                        <span>{manager.displayName}</span>
                      </span>
                      <span className="text-xs text-zinc-400 dark:text-zinc-500 tabular-nums">
                        #{manager.rank} &middot; {manager.points}pts
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
